import React, { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodePopoverMenu — Context menu for duplicate/delete block actions.
 * Used by: Conditional Path, Conditional Logic, Conditional WF Paths.
 */

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: NODE_UI.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: NODE_UI.shadow,
    minWidth: 140,
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  padding: '6px 12px',
  gap: 8,
  '& .MuiListItemIcon-root': {
    minWidth: 'unset',
    color: theme.palette.text.secondary,
  },
  '&.destructive': {
    color: theme.palette.error.main,
    '& .MuiListItemIcon-root': {
      color: theme.palette.error.main,
    },
  },
}));

const NodePopoverMenu = React.memo(function NodePopoverMenu({
  items = [],
  icon,
  size = 'small',
  sx,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = useCallback((e) => {
    stopPropagation(e);
    setAnchorEl(e.currentTarget);
  }, []);

  const handleClose = useCallback((e) => {
    if (e) stopPropagation(e);
    setAnchorEl(null);
  }, []);

  const handleItemClick = useCallback((item, e) => {
    stopPropagation(e);
    setAnchorEl(null);
    item.onClick?.();
  }, []);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      <IconButton size={size} onClick={handleOpen} sx={{ p: '4px', color: 'text.secondary', ...sx }}>
        {icon || <MoreVertIcon sx={{ fontSize: 16 }} />}
      </IconButton>
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {items.map((item, i) => (
          <StyledMenuItem
            key={item.label || i}
            onClick={(e) => handleItemClick(item, e)}
            disabled={item.disabled}
            className={item.destructive ? 'destructive' : ''}
          >
            {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
            <ListItemText primaryTypographyProps={{ fontSize: NODE_UI.fontSize.value }}>
              {item.label}
            </ListItemText>
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </div>
  );
});

export default NodePopoverMenu;
